import { Component, computed, input, model } from "@angular/core";
import { EChartsOption } from "echarts/types/dist/shared";

import { SegyFieldOption, SegyScanResponse } from "../../../../models/segy";
import { TRACE_NUMBER_OPTION } from "../field-select/field-select.component";

/**
 * Cross-plot any two header values from the shared header scan (Y against
 * the trace number or another field), optionally coloured by a third — all
 * chosen with the same field picker used everywhere on the SEG-Y page.
 */
@Component({
  selector: "app-segy-header-crossplot",
  templateUrl: "./header-crossplot.component.html",
  styleUrl: "./header-crossplot.component.less",
  standalone: false,
})
export class SegyHeaderCrossplotComponent {
  public readonly scan = input<SegyScanResponse | null>(null);
  public readonly fieldOptions = input<SegyFieldOption[]>([]);
  public readonly xField = model<string>("trace");
  public readonly yField = model<string>("ffid");
  public readonly colorField = model<string | null>(null);

  protected readonly traceOption = [TRACE_NUMBER_OPTION];

  private readonly labels = computed(() => {
    const map = new Map<string, string>([["trace", "trace number"]]);
    for (const o of this.fieldOptions()) map.set(o.value, o.label);
    return map;
  });

  protected readonly options = computed<EChartsOption | null>(() =>
    this.buildOptions(),
  );

  private values(scan: SegyScanResponse, field: string | null) {
    if (!field) return null;
    return field === "trace" ? scan.trace : (scan.fields[field] ?? null);
  }

  private buildOptions(): EChartsOption | null {
    const scan = this.scan();
    if (!scan) return null;
    const xs = this.values(scan, this.xField());
    const ys = this.values(scan, this.yField());
    if (!xs || !ys) return null;
    const cs = this.values(scan, this.colorField());

    const data = xs.map((x, i) => [x, ys[i], cs ? cs[i] : null]);
    // a header against the (monotonic) trace number reads best as a line;
    // header-vs-header (or anything coloured) is a relationship scatter
    const asLine = this.xField() === "trace" && !cs;
    const xName = this.labels().get(this.xField()) ?? this.xField();
    const yName = this.labels().get(this.yField()) ?? this.yField();
    const cName = cs ? (this.labels().get(this.colorField()!) ?? "") : "";

    let cMin = Infinity;
    let cMax = -Infinity;
    if (cs) {
      for (const c of cs) {
        if (c < cMin) cMin = c;
        if (c > cMax) cMax = c;
      }
    }

    // crosshair label: trim float noise but keep genuine decimals
    const axisReadout = (p: { value: number | string }) =>
      typeof p.value === "number" ? String(+p.value.toFixed(2)) : `${p.value}`;

    return {
      animation: false,
      grid: { left: 10, right: cs ? 90 : 20, top: 16, bottom: 55, containLabel: true },
      tooltip: {
        // the line render hides its symbols, so per-point hover has nothing to
        // hit — resolve against the axis (nearest trace) instead
        trigger: asLine ? "axis" : "item",
        axisPointer: { type: "cross" },
        formatter: (p: any) => {
          const point = Array.isArray(p) ? p[0] : p;
          const [xx, yy, cc] = point?.value ?? [];
          let html = `${xName}: ${xx}<br/>${yName}: ${yy}`;
          if (cs) html += `<br/>${cName}: ${cc}`;
          return html;
        },
      },
      ...(cs && isFinite(cMin)
        ? {
            visualMap: {
              type: "continuous",
              dimension: 2,
              min: cMin,
              max: cMax === cMin ? cMin + 1 : cMax,
              calculable: true,
              precision: 2,
              right: 0,
              top: "middle",
              itemHeight: 200,
              text: [cName, ""],
              inRange: {
                color: ["#440154", "#3b528b", "#21918c", "#5ec962", "#fde725"],
              },
            },
          }
        : {}),
      xAxis: {
        type: "value",
        name: xName,
        nameLocation: "middle",
        nameGap: 28,
        scale: true,
        axisPointer: { show: true, label: { formatter: axisReadout } },
      },
      yAxis: {
        type: "value",
        name: yName,
        nameLocation: "middle",
        nameGap: 60,
        scale: true,
        axisPointer: { show: true, label: { formatter: axisReadout } },
      },
      dataZoom: [
        { type: "inside", xAxisIndex: 0, filterMode: "none" },
        {
          type: "slider",
          xAxisIndex: 0,
          height: 18,
          bottom: 5,
          filterMode: "none",
        },
      ],
      series: [
        {
          name: `${yName} vs ${xName}`,
          type: asLine ? "line" : "scatter",
          showSymbol: !asLine,
          symbolSize: 5,
          sampling: "lttb",
          animation: false,
          large: !cs,
          progressive: 20000,
          color: "#1890ff",
          data,
        },
      ],
    } as EChartsOption;
  }
}
